/**
 * Money.
 *
 * The v1 contract sends `numeric` columns as strings — `"12.50"`, not `12.5`
 * (BACKEND.md §8, FRONTEND.md §14.7). Every one of them passes through here, so
 * that no feature file ever reaches for `parseFloat` and no total is assembled
 * out of binary floats.
 *
 * Amounts are carried as **integer cents**. `0.1 + 0.2` is not `0.3` in a
 * double, and the order builder adds a dozen line totals together before the
 * customer is charged — legacy assembles those sums in floats, which is exactly
 * the drift §14.7 warns about.
 */

const CENTS_PER_EURO = 100

/** An amount as it arrives from the API, or as a form produced it. */
export type MoneyInput = string | number | null | undefined

const EURO_FORMAT = new Intl.NumberFormat('sk-SK', {
  style: 'currency',
  currency: 'EUR',
})

const PLAIN_FORMAT = new Intl.NumberFormat('sk-SK', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Parses to integer cents, rounding half away from zero — the rule Slovak
 * invoicing uses, and the one `toFixed` does not reliably follow for negatives.
 *
 * Returns 0 for null, undefined, empty and unparseable input rather than NaN:
 * the dumps hold legacy rows with empty numerics, and a NaN would render as
 * "NaN €" on a receipt instead of "0,00 €".
 */
export function parseMoneyToCents(value: MoneyInput): number {
  if (value === null || value === undefined || value === '') return 0

  const amount = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  if (!Number.isFinite(amount)) return 0

  const scaled = amount * CENTS_PER_EURO
  return scaled < 0 ? -Math.round(-scaled) : Math.round(scaled)
}

/** Cents back to a number of euros — for charts and anything that needs a scalar. */
export function centsToEuros(cents: number): number {
  return cents / CENTS_PER_EURO
}

/** `"12.50"` → `"12,50 €"`. Byte-identical to the legacy `formatCurrency`. */
export function formatMoney(value: MoneyInput): string {
  return EURO_FORMAT.format(centsToEuros(parseMoneyToCents(value)))
}

/** The same number without the currency symbol, for table columns that carry their own. */
export function formatAmount(value: MoneyInput): string {
  return PLAIN_FORMAT.format(centsToEuros(parseMoneyToCents(value)))
}

/**
 * `2450` → `"24,50 €"`, for an amount that has already been parsed.
 *
 * `formatMoney` takes what the API sent; this takes what `sumCents` produced.
 * Without it a caller holding cents has to divide by 100 and hand the float
 * back to the parser, which is the round trip §14.7 exists to stop people
 * writing by hand.
 */
export function formatCents(cents: number): string {
  return EURO_FORMAT.format(centsToEuros(cents))
}

/** Exact addition. Use this instead of summing parsed floats. */
export function sumCents(...amounts: MoneyInput[]): number {
  return amounts.reduce<number>((total, amount) => total + parseMoneyToCents(amount), 0)
}

/**
 * A line total: unit price × quantity, exactly.
 *
 * Quantity may be fractional (250 g of something priced per kilo), so the
 * product is rounded once, at the end, rather than accumulating.
 */
export function lineTotalCents(unitPrice: MoneyInput, quantity: number): number {
  const product = parseMoneyToCents(unitPrice) * quantity
  return product < 0 ? -Math.round(-product) : Math.round(product)
}

/**
 * A multiplicand — a quantity, a unit price, a rate, a percentage — as the
 * number it is, **not** rounded to the cent.
 *
 * This is the other half of §14.7 and it is the half that is easy to get wrong.
 * `parseMoneyToCents` is right for an amount; it is wrong for anything about to
 * be multiplied, because rounding a multiplicand first invents money. That is
 * FB-056: `10,77 kg × 5,3333 €` is 57,44 €, but rounding the unit price to
 * 5,33 € first gives 57,40 €. The same trap caught the pizza cost estimate —
 * 0,1472 € × 4,84 is 71 cents, while 15 cents × 4,84 is 73.
 *
 * Only the product becomes money. Reach for `lineTotalCents` when that product
 * is a straightforward price × quantity; reach for this when it is not.
 */
export function rateOf(value: MoneyInput): number {
  if (value === null || value === undefined || value === '') return 0
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

/** What to send back to the API: the string shape v1 expects. */
export function centsToApiString(cents: number): string {
  const sign = cents < 0 ? '-' : ''
  const absolute = Math.abs(cents)
  const euros = Math.floor(absolute / CENTS_PER_EURO)
  const remainder = String(absolute % CENTS_PER_EURO).padStart(2, '0')
  return `${sign}${euros}.${remainder}`
}

/** A percentage of an amount, rounded to the cent — VAT, margins, discounts. */
export function percentageOfCents(cents: number, percentage: MoneyInput): number {
  const rate = parseMoneyToCents(percentage) / CENTS_PER_EURO
  const product = (cents * rate) / 100
  return product < 0 ? -Math.round(-product) : Math.round(product)
}

/** Slovakia withdrew the 1c and 2c coins in 2022; cash settles on 5-cent steps. */
const CASH_STEP_CENTS = 5

/**
 * Rounds to the nearest 5 cents — legacy's `Math.round(x * 20) / 20`, in cents.
 *
 * The order builder applies this to the manual discount and to the order total,
 * so a cart's total is a number a till can actually be paid. It is not
 * cosmetic: applying it in a different place, or one time fewer, changes what
 * the customer is charged.
 *
 * Halves round away from zero, matching `Math.round` on the positive side and
 * staying symmetric on the negative one.
 */
export function roundToCashStep(cents: number): number {
  const steps = cents / CASH_STEP_CENTS
  const rounded = steps < 0 ? -Math.round(-steps) : Math.round(steps)
  return rounded * CASH_STEP_CENTS
}
